import { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Building2,
  Image as ImageIcon,
  Send,
  CheckCircle2,
  Facebook,
  Instagram,
  Loader2,
  ChevronDown,
  Check,
} from "lucide-react";
import { COMPANY, SERVICES } from "../data/site";
import { toast } from "../components/ui/sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Label } from "../components/ui/label";
import SEO from "../components/SEO";
import FAQ from "../components/FAQ";
import { KONTAKT_FAQS } from "../data/faqs";

const FORM_ENDPOINT = "https://forms.elido.nu/api/contact";

function getFormSiteId() {
  if (
    typeof window !== "undefined" &&
    window.location.hostname.endsWith(".pages.dev")
  ) {
    return "tradfallarna-test";
  }
  return "tradfallarna";
}

function createSubmissionId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `tf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

const initialState = {
  name: "",
  phone: "",
  email: "",
  city: "",
  streetAddress: "",
  services: [], // multi-select: list of service slugs
  description: "",
  image: null,
  website: "", // honeypot
};

export default function Kontakt() {
  const [form, setForm] = useState(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submissionId, setSubmissionId] = useState(() => createSubmissionId());

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Ange ditt namn";
    if (!form.phone.trim()) e.phone = "Ange ditt telefonnummer";
    if (!form.city.trim()) e.city = "Ange ort";
    if (!form.streetAddress.trim())
      e.streetAddress = "Ange gatuadress";
    if (!form.services || form.services.length === 0)
      e.services = "Välj minst en tjänst";
    if (!form.description.trim() || form.description.trim().length < 10)
      e.description = "Beskriv jobbet med några ord (minst 10 tecken)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const toggleService = (slug) => {
    setForm((prev) => {
      const exists = prev.services.includes(slug);
      const next = exists
        ? prev.services.filter((s) => s !== slug)
        : [...prev.services, slug];
      return { ...prev, services: next };
    });
    if (errors.services) setErrors((prev) => ({ ...prev, services: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Fyll i de obligatoriska fälten");
      return;
    }
    setSubmitting(true);
    try {
      const serviceLabels = form.services
        .map((slug) => {
          const service = SERVICES.find((item) => item.slug === slug);
          return service?.title || (slug === "ovrigt" ? "Annat / vet ej" : slug);
        })
        .join(", ");

      const fd = new FormData();
      fd.append("site_id", getFormSiteId());
      fd.append("submission_id", submissionId);
      fd.append("website", form.website || "");
      fd.append("subject", `Offertförfrågan – ${serviceLabels}`);
      fd.append("name", form.name.trim());
      fd.append("phone", form.phone.trim());
      if (form.email.trim()) fd.append("email", form.email.trim());
      fd.append("city", form.city.trim());
      fd.append("street_address", form.streetAddress.trim());
      fd.append("service", form.services.join(","));
      fd.append("description", form.description.trim());
      fd.append(
        "message",
        [
          `Ort: ${form.city.trim()}`,
          `Gatuadress: ${form.streetAddress.trim()}`,
          `Tjänst: ${serviceLabels}`,
          "",
          "Beskrivning:",
          form.description.trim(),
        ].join("\n"),
      );
      if (form.image) fd.append("image", form.image);

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        body: fd,
      });

      let data = {};
      try {
        data = await response.json();
      } catch {
        // The status check below handles non-JSON failures.
      }

      if (
        !response.ok ||
        !data.ok ||
        !data.emailed ||
        data.stored !== true
      ) {
        throw new Error(
          data.detail ||
            "Något gick fel. Försök igen om en stund eller ring oss direkt.",
        );
      }

      setSubmitted(true);
      toast.success("Tack! Din förfrågan är skickad.", {
        description: "Vi återkommer så snart vi kan.",
      });
      setForm(initialState);
      setSubmissionId(createSubmissionId());
    } catch (err) {
      const msg =
        err?.message ||
        "Något gick fel. Försök igen om en stund eller ring oss direkt.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="kontakt-page" className="bg-[#F7F5F0]">
      <SEO
        title="Kontakt och offert | Trädfällarna Laholm AB"
        description="Kontakta Trädfällarna Laholm AB för offert på trädfällning, beskärning, stubbfräsning, bortforsling, snöröjning och markarbeten."
        path="/kontakt"
      />
      {/* Hero */}
      <section className="relative bg-[#1B2A1E] text-white tf-grain overflow-hidden">
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <img
            src="/images/tradvard.webp"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A1E] via-[#1B2A1E]/95 to-[#1B2A1E]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <span className="eyebrow !text-[#F2A57C]">Kontakt</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight max-w-3xl">
            Behöver du hjälp med ett träd?
          </h1>
          <p className="mt-5 text-[#D9DCD3] text-lg leading-relaxed max-w-2xl">
            Beskriv vad du behöver hjälp med så återkommer vi med nästa steg.
            Skicka gärna bild på trädet och berätta var det står, så blir det
            lättare att göra en första bedömning.
          </p>
        </div>
      </section>

      {/* Team section */}
      <section data-testid="team-section" className="section-pad bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="relative rounded-2xl overflow-hidden bg-[#1B2A1E] aspect-[4/3] lg:aspect-[5/4]">
              <img
                src="/images/team.jpg"
                alt="Trädfällarna Laholm — teamet i skogen"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 text-[#1B2A1E] text-xs font-bold uppercase tracking-[0.14em]">
                  <span className="w-2 h-2 rounded-full bg-[#D95D2E]" /> Laholm
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1B2A1E]/85 backdrop-blur-sm text-white text-xs font-bold uppercase tracking-[0.14em]">
                  Erfarna arborister
                </span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <span className="eyebrow">Vi som arbetar</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1B2A1E] leading-[1.05] tracking-tight">
              Lokala arborister du kan ringa direkt
            </h2>
            <p className="mt-5 text-[#3a4338] text-lg leading-relaxed">
              Vi är ett litet, lokalt team i Laholm med fokus på säker
              trädfällning, beskärning och stubbfräsning. Inga callcenter, inga
              mellanhänder — du pratar direkt med oss som utför arbetet.
            </p>
            <ul className="mt-6 space-y-2.5">
              {[
                "Utbildade och försäkrade arborister",
                "Egen utrustning, kranbil och stubbfräs",
                "Tydlig offert innan vi börjar — alltid",
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 text-[#18221A] font-medium"
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1B2A1E] text-[#D95D2E] shrink-0 mt-0.5">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-3.5 h-3.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-pad" id="offert">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#E2E0D8] rounded-xl p-6 sm:p-8 lg:p-10">
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#1B2A1E] mb-2">
                Skicka offertförfrågan
              </h2>
              <p className="text-[#3a4338] mb-7">
                Vi återkommer så snart vi kan. Fält märkta med * är obligatoriska.
              </p>

              {submitted ? (
                <div
                  data-testid="contact-form-success"
                  className="flex items-start gap-4 bg-[#EFEBDF] border border-[#D95D2E]/30 rounded-lg p-6"
                >
                  <CheckCircle2 className="w-7 h-7 text-[#D95D2E] shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading font-bold text-lg text-[#1B2A1E] mb-1">
                      Tack för din förfrågan!
                    </h3>
                    <p className="text-[#3a4338] mb-4">
                      Vi har tagit emot dina uppgifter och återkommer så snart vi
                      kan, oftast inom 1–2 vardagar.
                    </p>
                    <button
                      type="button"
                      data-testid="contact-form-reset"
                      onClick={() => setSubmitted(false)}
                      className="inline-flex items-center gap-2 text-[#D95D2E] font-semibold hover:underline"
                    >
                      Skicka en till förfrågan
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="contact-form"
                  noValidate
                  className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  <Field
                    label="Namn *"
                    error={errors.name}
                    id="name"
                    inputProps={{
                      "data-testid": "contact-input-name",
                      type: "text",
                      autoComplete: "name",
                      value: form.name,
                      onChange: (e) => update("name", e.target.value),
                    }}
                  />
                  <Field
                    label="Telefon *"
                    error={errors.phone}
                    id="phone"
                    inputProps={{
                      "data-testid": "contact-input-phone",
                      type: "tel",
                      autoComplete: "tel",
                      value: form.phone,
                      onChange: (e) => update("phone", e.target.value),
                    }}
                  />
                  <Field
                    label="E-post"
                    id="email"
                    inputProps={{
                      "data-testid": "contact-input-email",
                      type: "email",
                      autoComplete: "email",
                      value: form.email,
                      onChange: (e) => update("email", e.target.value),
                    }}
                  />
                  <Field
                    label="Ort *"
                    id="city"
                    error={errors.city}
                    inputProps={{
                      "data-testid": "contact-input-city",
                      type: "text",
                      autoComplete: "address-level2",
                      placeholder: "T.ex. Laholm",
                      value: form.city,
                      onChange: (e) => update("city", e.target.value),
                    }}
                  />
                  <Field
                    label="Gatuadress *"
                    id="streetAddress"
                    error={errors.streetAddress}
                    inputProps={{
                      "data-testid": "contact-input-street",
                      type: "text",
                      autoComplete: "street-address",
                      placeholder: "T.ex. Storgatan 5",
                      value: form.streetAddress,
                      onChange: (e) => update("streetAddress", e.target.value),
                    }}
                  />

                  <div className="sm:col-span-2">
                    <Label
                      htmlFor="services-trigger"
                      className="text-sm font-heading font-semibold text-[#1B2A1E] mb-1.5 block"
                    >
                      Vilken / vilka tjänster gäller det? *
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          id="services-trigger"
                          data-testid="contact-services-trigger"
                          className={`w-full h-auto min-h-11 bg-white border rounded-lg px-3.5 py-2 text-left flex items-center justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-[#D95D2E] focus:border-transparent ${
                            errors.services
                              ? "border-red-500"
                              : "border-[#E2E0D8]"
                          }`}
                        >
                          {form.services.length === 0 ? (
                            <span className="text-[#9aa098]">
                              Välj en eller flera tjänster
                            </span>
                          ) : (
                            <span className="flex flex-wrap gap-1.5 py-0.5">
                              {form.services.map((slug) => {
                                const svc = SERVICES.find(
                                  (s) => s.slug === slug,
                                );
                                const label =
                                  svc?.title ||
                                  (slug === "ovrigt" ? "Annat / vet ej" : slug);
                                return (
                                  <span
                                    key={slug}
                                    data-testid={`contact-service-chip-${slug}`}
                                    className="inline-flex items-center gap-1 bg-[#F7F5F0] border border-[#E2E0D8] rounded-md px-2 py-0.5 text-xs font-semibold text-[#1B2A1E]"
                                  >
                                    {label}
                                  </span>
                                );
                              })}
                            </span>
                          )}
                          <ChevronDown className="w-4 h-4 text-[#5A6258] shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        sideOffset={6}
                        data-testid="contact-services-menu"
                        className="w-[var(--radix-popover-trigger-width)] p-1.5 bg-white border-[#E2E0D8]"
                      >
                        <ul className="max-h-72 overflow-y-auto">
                          {SERVICES.map((s) => {
                            const checked = form.services.includes(s.slug);
                            return (
                              <li key={s.slug}>
                                <button
                                  type="button"
                                  onClick={() => toggleService(s.slug)}
                                  data-testid={`contact-service-option-${s.slug}`}
                                  aria-pressed={checked}
                                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-left text-sm font-medium transition-colors ${
                                    checked
                                      ? "bg-[#FFF1E8] text-[#7A2E10]"
                                      : "text-[#18221A] hover:bg-[#F7F5F0]"
                                  }`}
                                >
                                  <span>{s.title}</span>
                                  {checked && (
                                    <Check className="w-4 h-4 text-[#D95D2E] shrink-0" />
                                  )}
                                </button>
                              </li>
                            );
                          })}
                          <li>
                            <button
                              type="button"
                              onClick={() => toggleService("ovrigt")}
                              data-testid="contact-service-option-ovrigt"
                              aria-pressed={form.services.includes("ovrigt")}
                              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-left text-sm font-medium transition-colors ${
                                form.services.includes("ovrigt")
                                  ? "bg-[#FFF1E8] text-[#7A2E10]"
                                  : "text-[#18221A] hover:bg-[#F7F5F0]"
                              }`}
                            >
                              <span>Annat / vet ej</span>
                              {form.services.includes("ovrigt") && (
                                <Check className="w-4 h-4 text-[#D95D2E] shrink-0" />
                              )}
                            </button>
                          </li>
                        </ul>
                      </PopoverContent>
                    </Popover>
                    {errors.services && (
                      <p className="mt-1.5 text-sm text-red-600">
                        {errors.services}
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <Label
                      htmlFor="description"
                      className="text-sm font-heading font-semibold text-[#1B2A1E] mb-1.5 block"
                    >
                      Beskriv jobbet *
                    </Label>
                    <textarea
                      id="description"
                      data-testid="contact-input-description"
                      rows={5}
                      value={form.description}
                      onChange={(e) => update("description", e.target.value)}
                      placeholder="Berätta gärna vad du behöver hjälp med, ungefärlig storlek, hur trädet står m.m."
                      className={`w-full px-3.5 py-3 rounded-lg bg-white border text-[#18221A] placeholder:text-[#9aa098] focus:outline-none focus:ring-2 focus:ring-[#D95D2E] focus:border-transparent resize-none ${
                        errors.description
                          ? "border-red-500"
                          : "border-[#E2E0D8]"
                      }`}
                    />
                    {errors.description && (
                      <p className="mt-1.5 text-sm text-red-600">
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <Label
                      htmlFor="image"
                      className="text-sm font-heading font-semibold text-[#1B2A1E] mb-1.5 block"
                    >
                      Bifoga bild (valfritt)
                    </Label>
                    <label
                      htmlFor="image"
                      className="flex items-center gap-3 cursor-pointer bg-[#F7F5F0] border border-dashed border-[#E2E0D8] hover:border-[#D95D2E] rounded-lg px-4 py-4 transition-colors"
                    >
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white text-[#D95D2E] border border-[#E2E0D8]">
                        <ImageIcon className="w-5 h-5" />
                      </span>
                      <span className="flex-1 text-sm">
                        <span className="block font-semibold text-[#1B2A1E]">
                          {form.image?.name || "Klicka för att välja bild"}
                        </span>
                        <span className="block text-[#5A6258]">
                          Skicka gärna en bild på trädet eller platsen. JPG, PNG eller HEIC (max ca 10 MB).
                        </span>
                      </span>
                    </label>
                    <input
                      id="image"
                      data-testid="contact-input-image"
                      type="file"
                      accept="image/*,.heic,.heif"
                      onChange={(e) =>
                        update("image", e.target.files?.[0] || null)
                      }
                      className="sr-only"
                    />
                  </div>

                  {/* Honeypot */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Lämna tomt</label>
                    <input
                      id="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => update("website", e.target.value)}
                    />
                  </div>

                  <div className="sm:col-span-2 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-button"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#D95D2E] hover:bg-[#B84B22] text-white font-semibold px-7 py-4 rounded-lg transition-colors disabled:opacity-60"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      {submitting ? "Skickar…" : "Skicka förfrågan"}
                    </button>
                    <p className="mt-3 text-xs text-[#5A6258]">
                      Genom att skicka godkänner du att vi kontaktar dig om din
                      förfrågan.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar info */}
          <aside className="lg:col-span-5 space-y-4">
            <div
              data-testid="kontakt-info-phones"
              className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden"
            >
              <div className="p-5 pb-3">
                <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#5A6258]">
                  Telefon
                </div>
                <p className="text-sm text-[#5A6258] mt-1">
                  Ring direkt — vi svarar mellan uppdrag.
                </p>
              </div>
              <ul className="divide-y divide-[#E2E0D8] border-t border-[#E2E0D8]">
                <li>
                  <a
                    href="tel:+46703422162"
                    data-testid="kontakt-phone-hampus"
                    className="flex items-center gap-3 p-4 hover:bg-[#FFF6F0] transition-colors"
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#EFEBDF] text-[#D95D2E] shrink-0">
                      <Phone className="w-4 h-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] uppercase tracking-[0.14em] font-bold text-[#5A6258]">
                        Hampus
                      </span>
                      <span className="block font-heading font-extrabold text-[#1B2A1E] text-lg">
                        070-342 21 62
                      </span>
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+46707552597"
                    data-testid="kontakt-phone-joakim"
                    className="flex items-center gap-3 p-4 hover:bg-[#FFF6F0] transition-colors"
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#EFEBDF] text-[#D95D2E] shrink-0">
                      <Phone className="w-4 h-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] uppercase tracking-[0.14em] font-bold text-[#5A6258]">
                        Joakim
                      </span>
                      <span className="block font-heading font-extrabold text-[#1B2A1E] text-lg">
                        070-755 25 97
                      </span>
                    </span>
                  </a>
                </li>
              </ul>
            </div>
            <InfoCard
              icon={Mail}
              label="E-post"
              testId="kontakt-info-email"
              href={`mailto:${COMPANY.email}`}
              value={COMPANY.email}
              subtitle="Skriv några rader så återkommer vi."
            />
            <InfoCard
              icon={MapPin}
              label="Besöksadress"
              testId="kontakt-info-address"
              value={`${COMPANY.address.street}, ${COMPANY.address.zip} ${COMPANY.address.city}`}
              subtitle="Vi utgår från Laholm."
            />
            <InfoCard
              icon={Building2}
              label="Företag"
              testId="kontakt-info-company"
              value={COMPANY.name}
              subtitle={`Org. nr ${COMPANY.org}`}
            />

            <div
              data-testid="kontakt-social"
              className="bg-white border border-[#E2E0D8] rounded-xl p-5"
            >
              <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#5A6258] mb-3">
                Följ oss
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={COMPANY.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="kontakt-social-facebook"
                  className="inline-flex items-center gap-2.5 bg-[#1B2A1E] hover:bg-[#D95D2E] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm"
                >
                  <Facebook className="w-4 h-4" strokeWidth={2.2} />
                  Facebook
                </a>
                <a
                  href={COMPANY.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="kontakt-social-instagram"
                  className="inline-flex items-center gap-2.5 bg-[#1B2A1E] hover:bg-[#D95D2E] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm"
                >
                  <Instagram className="w-4 h-4" strokeWidth={2.2} />
                  Instagram
                </a>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden border border-[#E2E0D8] bg-white">
              <div className="aspect-[4/3] w-full">
                <iframe
                  data-testid="kontakt-map"
                  title="Karta över Laholms kommun"
                  src="https://www.google.com/maps?q=Laholms+kommun,Sweden&z=10&hl=sv&output=embed"
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="p-4 flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#D95D2E] shrink-0" />
                <div className="text-sm text-[#3a4338]">
                  Vi utgår från Laholm och tar uppdrag i hela kommunen med
                  omnejd.
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <FAQ
        id="kontakt-faq"
        title="Vanliga frågor inför offert"
        intro="Här är några av de frågor vi oftast får innan vi skickar offert. Tveka inte att ringa eller skriva — vi svarar gärna direkt."
        items={KONTAKT_FAQS}
      />
    </div>
  );
}

function Field({ label, id, error, inputProps }) {
  return (
    <div>
      <Label
        htmlFor={id}
        className="text-sm font-heading font-semibold text-[#1B2A1E] mb-1.5 block"
      >
        {label}
      </Label>
      <input
        id={id}
        {...inputProps}
        className={`w-full h-11 px-3.5 rounded-lg bg-white border text-[#18221A] placeholder:text-[#9aa098] focus:outline-none focus:ring-2 focus:ring-[#D95D2E] focus:border-transparent ${
          error ? "border-red-500" : "border-[#E2E0D8]"
        }`}
      />
      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function InfoCard({ icon: Icon, label, value, subtitle, href, testId }) {
  const Tag = href ? "a" : "div";
  return (
    <Tag
      href={href}
      data-testid={testId}
      className={`block bg-white border border-[#E2E0D8] rounded-xl p-5 ${
        href ? "hover:border-[#D95D2E] transition-colors" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-[#1B2A1E] text-[#D95D2E] shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#5A6258]">
            {label}
          </div>
          <div className="font-heading font-bold text-[#1B2A1E] text-lg leading-tight mt-0.5 break-words">
            {value}
          </div>
          {subtitle && (
            <div className="mt-1 text-sm text-[#3a4338]">{subtitle}</div>
          )}
        </div>
      </div>
    </Tag>
  );
}
